import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LuLogIn } from 'react-icons/lu';
import type { LessonInfo } from '../../shared/protocol';
import { LIMITS } from '../../shared/protocol';
import { CenterCard, NovaMark } from '../components/ui';
import { storage } from '../lib/storage';
import { LessonRoom } from '../room/LessonRoom';

function JoinScreen({ info, isTeacher, onJoin }: { info: LessonInfo; isTeacher: boolean; onJoin: (name: string) => void }) {
  const [name, setName] = useState(() => storage.name());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const clean = name.trim().replace(/\s+/g, ' ');
    if (clean) onJoin(clean.slice(0, LIMITS.maxNameLength));
  };
  return (
    <div className="center-screen">
      <form className="card join-card" onSubmit={submit}>
        <NovaMark size={40} />
        <p className="eyebrow">{isTeacher ? 'Öğretmen olarak giriyorsunuz' : 'Canlı derse katılıyorsunuz'}</p>
        <h1>{info.title}</h1>
        <p className="muted small">{info.pageCount ? `${info.pageCount} sayfa · ` : ''}{info.pdfName}</p>
        <label className="field">
          <span>Adınız</span>
          <input
            id="join-name"
            autoFocus
            value={name}
            maxLength={LIMITS.maxNameLength}
            placeholder={isTeacher ? 'ör. Ömer Hoca' : 'ör. Ayşe Yılmaz'}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={!name.trim()}>
          <LuLogIn /> Derse gir
        </button>
        <p className="muted small join-legal">
          Yazdığınız ad derstekilere görünür. Ayrıntılar için{' '}
          <a href="/kvkk" target="_blank" rel="noopener">
            KVKK Aydınlatma Metni
          </a>
          .
        </p>
      </form>
    </div>
  );
}

export function LessonPage({ lessonId, teacherKey }: { lessonId: string; teacherKey?: string }) {
  const [info, setInfo] = useState<LessonInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(() => storage.joinedAs(lessonId));

  useEffect(() => {
    let alive = true;
    fetch(`/api/lessons/${lessonId}`)
      .then(async (r) => {
        const body = (await r.json().catch(() => ({}))) as Partial<LessonInfo> & { error?: string };
        if (!r.ok) throw new Error(body.error ?? 'Ders bulunamadı.');
        return body as LessonInfo;
      })
      .then(
        (i) => alive && setInfo(i),
        (err: Error) => alive && setError(err.message),
      );
    return () => {
      alive = false;
    };
  }, [lessonId]);

  if (error) {
    return (
      <CenterCard title="Ders açılamadı" text={error}>
        <a className="btn btn-primary" href="/">
          Ana sayfa
        </a>
      </CenterCard>
    );
  }
  if (!info) return <CenterCard title="Ders yükleniyor…" />;
  if (!name) {
    return (
      <JoinScreen
        info={info}
        isTeacher={!!teacherKey}
        onJoin={(n) => {
          storage.setName(n);
          storage.markJoined(lessonId, n);
          setName(n);
        }}
      />
    );
  }
  return <LessonRoom lessonId={lessonId} teacherKey={teacherKey} name={name} info={info} />;
}
